import { includes, map, take } from 'lodash';

import { IBudgetSlot } from '../types/store/budget';
import { IRouterData, IRouterSlot } from '../types/store/routerData';
import { ISlotItem, ISlotItemPort, ISlotItemSupply, ISlotsItems } from '../types/store/slotsItems';

const getOccupedSlotItemsByType = (portItem: ISlotItemPort, budgetSlots: IBudgetSlot[]) =>
  budgetSlots.filter(budgetSlot => budgetSlot.filled === portItem.id);

const getMaxWattsFront = (frontBudgetSlots: IBudgetSlot[], slotsItems: ISlotsItems) =>
  frontBudgetSlots.reduce(
    (maxWattsNow, slot) =>
      maxWattsNow < (slotsItems[slot.filled] as ISlotItemPort).minWatts
        ? (slotsItems[slot.filled] as ISlotItemPort).minWatts
        : maxWattsNow,
    0
  );

const getMaxWattsBack = (backBudgetSlots: IBudgetSlot[], slotsItems: ISlotsItems) =>
  backBudgetSlots.reduce(
    (maxWattsNow: number, slot: IBudgetSlot) =>
      (slotsItems[slot.filled] as ISlotItemSupply).maxWatts > maxWattsNow
        ? (slotsItems[slot.filled] as ISlotItemSupply).maxWatts
        : maxWattsNow,
    0
  );

const getPowerSupply = (
  frontBudgetSlots: IBudgetSlot[],
  slotsItems: ISlotsItems,
  possibleSupplyIds: string[]
): ISlotItemSupply => {
  const maxWatts = getMaxWattsFront(frontBudgetSlots, slotsItems);
  return possibleSupplyIds
    .map(supplyId => slotsItems[supplyId] as ISlotItemSupply)
    .filter(itemSupply => itemSupply.maxWatts >= maxWatts)
    .reduce((powerSupply, itemSupply) => (powerSupply.maxWatts > itemSupply.maxWatts ? itemSupply : powerSupply));
};

const isEnoughPower = (
  frontBudgetSlots: IBudgetSlot[],
  slotsItems: ISlotsItems,
  slotSupplyItem: ISlotItemSupply
): boolean => slotSupplyItem.maxWatts >= getMaxWattsFront(frontBudgetSlots, slotsItems);

// slots
export const getRouterSlotsByTypes = (allSlotsInRouter: IRouterSlot[], slotTypeNames: string[]) =>
  allSlotsInRouter.filter(slotRouter => includes(slotTypeNames, slotRouter.type));

export const getAvailableRouterSlotsByType = (
  routerSlots: IRouterSlot[],
  budgetSlots: IBudgetSlot[],
  slotTypeNames?: string[]
) => {
  const availableRouterSlots = slotTypeNames ? getRouterSlotsByTypes(routerSlots, slotTypeNames) : routerSlots;
  const budgetSlotsNames = map(budgetSlots, 'name');
  return availableRouterSlots.filter(aRouterSlot => !includes(budgetSlotsNames, aRouterSlot.name));
};

export const getOccupedPortsBySlotNumberByPortItem = (portItem: ISlotItemPort, budgetSlots: IBudgetSlot[]) =>
  getOccupedSlotItemsByType(portItem, budgetSlots).reduce((sum, budgetSlot) => sum + portItem.portsBySlot, 0);

// supply

export const needMoreSupply = (
  frontBudgetSlots: IBudgetSlot[],
  backBudgetSlots: IBudgetSlot[],
  slotsItems: ISlotsItems
) => {
  const maxWatts = getMaxWattsBack(backBudgetSlots, slotsItems);
  return frontBudgetSlots.reduce(
    (needMorePower, slot) => ((slotsItems[slot.filled] as ISlotItemPort).minWatts > maxWatts ? true : needMorePower),
    false
  );
};

export const generatePowerSuplies = (
  frontSlotsBudget: IBudgetSlot[],
  backSlotsBudget: IBudgetSlot[],
  slotsItems: ISlotsItems,
  routerData: IRouterData
) => {
  const powerSupply = getPowerSupply(frontSlotsBudget, slotsItems, routerData.supplySupported);
  const supplyNumber = backSlotsBudget.length === 2 ? 2 : 1;
  return take(routerData.slots.back.slotsSpecs, supplyNumber).map(slot => ({
    name: slot.name,
    filled: powerSupply.id
  }));
};

// others

export const getAllItemsWithDisabled = (
  allSlotsItems: ISlotsItems,
  slotRouter: IRouterSlot,
  slotItem: ISlotItem,
  type: string
) =>
  Object.keys(allSlotsItems)
    .filter(slotItemKey => allSlotsItems[slotItemKey].type === type)
    .map(slotItemKey => ({
      id: slotItemKey,
      enabled: includes(allSlotsItems[slotItemKey].usedBy, slotRouter.type),
      item: allSlotsItems[slotItemKey],
      selected: slotItem && slotItemKey === slotItem.id
    }));

export const getAllItemsWithDisabledSupply = (
  frontSlotsBudget: IBudgetSlot[],
  allSlotsItems: ISlotsItems,
  slotRouter: IRouterSlot,
  slotItem: ISlotItemSupply,
  type: string
) =>
  Object.keys(allSlotsItems)
    .filter(slotItemKey => allSlotsItems[slotItemKey].type === type)
    .map(slotItemKey => ({
      id: slotItemKey,
      enabled: isEnoughPower(frontSlotsBudget, allSlotsItems, allSlotsItems[slotItemKey] as ISlotItemSupply),
      item: allSlotsItems[slotItemKey],
      selected: slotItem && slotItemKey === slotItem.id
    }));



// WEBPACK FOOTER //
// ./src/utils/manageRouterSlots.ts